// src/pages/languages/CreateLanguageSkill.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Save } from "lucide-react";
import { languageSkillsAPI } from "../../services/languageService";

const SKILL_TYPES = [
  { value: "VOCABULARY", label: "Vocabulary" },
  { value: "GRAMMAR", label: "Grammar" },
  { value: "READING", label: "Reading" },
  { value: "LISTENING", label: "Listening" },
  { value: "SPEAKING", label: "Speaking" },
  { value: "WRITING", label: "Writing" },
  { value: "MIXED", label: "Mixed (مسار متكامل)" },
];

const ORDER_TYPES = [
  { value: "SEQUENTIAL", label: "متسلسل (سهل ← صعب)" },
  { value: "RANDOM", label: "عشوائي" },
  { value: "CYCLIC", label: "دوري (Easy/Medium/Hard بالتبادل)" },
];

export default function CreateLanguageSkill() {
  // إنشاء: /dashboard/languages/categories/:categoryId/skills/create
  // تعديل: /dashboard/languages/skills/:skillId/edit
  const { categoryId, skillId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(skillId);

  const [form, setForm] = useState({
    skill_type: "VOCABULARY",
    title: "",
    description: "",
    order: 0,
    is_active: true,
    question_order_type: "SEQUENTIAL",
  });
  const [resolvedCategoryId, setResolvedCategoryId] = useState(categoryId);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) fetchSkill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  const fetchSkill = async () => {
    try {
      setLoading(true);
      const data = await languageSkillsAPI.getOne(skillId);
      setForm({
        skill_type: data.skill_type === "GENERAL_PATH" ? "MIXED" : (data.skill_type || "VOCABULARY"),
        title: data.title || "",
        description: data.description || "",
        order: data.order ?? 0,
        is_active: data.is_active ?? true,
        question_order_type: data.question_order_type || "SEQUENTIAL",
      });
      setResolvedCategoryId(data.category);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await languageSkillsAPI.update(skillId, form);
        navigate(`/dashboard/languages/skills/${skillId}`);
      } else {
        await languageSkillsAPI.create(categoryId, form);
        navigate(`/dashboard/languages/categories/${categoryId}/skills`);
      }
    } catch (err) {
      setError(
        err?.response?.data
          ? JSON.stringify(err.response.data)
          : "حدث خطأ أثناء الحفظ"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          onClick={() =>
            navigate(
              isEdit
                ? `/dashboard/languages/skills/${skillId}`
                : `/dashboard/languages/categories/${categoryId}/skills`
            )
          }
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowRight className="w-4 h-4" />
          رجوع
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? "تعديل المهارة" : "إضافة مهارة جديدة"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            نوع المهارة
          </label>
          <select
            value={form.skill_type}
            onChange={(e) => setForm({ ...form, skill_type: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {SKILL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            عنوان المهارة
          </label>
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            الوصف
          </label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            طريقة ترتيب الأسئلة
          </label>
          <select
            value={form.question_order_type}
            onChange={(e) =>
              setForm({ ...form, question_order_type: e.target.value })
            }
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ORDER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الترتيب
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: Number(e.target.value) })
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              مفعّلة
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          {saving ? "جارٍ الحفظ..." : "حفظ"}
        </button>
      </form>
    </div>
  );
}